import React, { useState, useEffect, useMemo } from 'react';
import {
    PencilSimple,
    Lightning,
    TrendUp,
    ChartBar,
    CalculatorIcon,
    Receipt,
    Buildings,
    FileArrowUp,
} from '@phosphor-icons/react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import UtilityDetailsModal from './UtilityDetailsModal';
// import UtilityDetailsModal from './UtilityDetailsModal';
import UtilityLoadCalculatorModal from './UtilityLoadCalculatorModal';
import { annualKwhFromMonthlyDailyFields } from '../../../../helpers/utilityMonthlyDailyToAnnual';
import { getOneButtonCapabilityValue } from '../../../../services/oneButtonServices/capabilityService';
import { isOpportunityActiveProductsRoofOnly } from '../../../../helpers/financeSelectors';

const MONTH_LONG = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString('default', { month: 'long' })
);
const MONTH_SHORT = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString('default', { month: 'short' })
);

/** Colorful palette for monthly bars (one per month) */
const MONTHLY_BAR_COLORS = [
    '#0ea5e9', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308',
    '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#a855f7',
];

export type UtilityDetailsCardProps = {
    /** Dashboard masonry card vs. content inside {@link UtilityDetailsPopupModal} */
    presentation?: 'dashboard' | 'popup';
    /** Popup only: toolbar actions rendered in the modal header */
    onPopupToolbar?: (toolbar: React.ReactNode) => void;
};

export const UtilityDetailsCard: React.FC<UtilityDetailsCardProps> = ({
    presentation = 'dashboard',
    onPopupToolbar,
}) => {
    const isPopup = presentation === 'popup';
    const theme = useOneButtonStore((s) => s.theme);
    const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
    const utilityCompanyName = useOneButtonStore((s) => s.utilityCompanyName);
    const utilityDetailsDirty = useOneButtonStore((s) => s.utilityDetailsDirty);
    const [annualUsageKwh, setAnnualUsageKwh] = useState('');
    const [estIncrease, setEstIncrease] = useState('');
    const [estIncreaseChecked, setEstIncreaseChecked] = useState(false);
    const [avgDailyKwh, setAvgDailyKwh] = useState('');
    const [allInRate, setAllInRate] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [loadCalculatorOpen, setCalculatorOpen] = useState(false);

    const onebuttonCapabilities = useOneButtonStore((s) => s.onebuttonCapabilities);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const utilityInfoAccess = getOneButtonCapabilityValue(onebuttonCapabilities, 'UI_Utility_Info_Access');
    const isUtilityDisabled = utilityInfoAccess !== 'Edit' && utilityInfoAccess !== 'View';

    const isRoofOnlyOpportunity = useMemo(
        () => isOpportunityActiveProductsRoofOnly(opportunityDetails),
        [opportunityDetails],
    );
    const editUtilityBlocked = isUtilityDisabled || isRoofOnlyOpportunity;

    const utilityToolbar = useMemo(
        () => (
            <div className="obd-metrics__action-group" role="group" aria-label="Utility actions">
                <button
                    type="button"
                    className="obd-merged-system__chip-btn"
                    onClick={() => setCalculatorOpen(true)}
                    aria-label="Load Calculator"
                >
                    <CalculatorIcon size={16} weight="bold" />
                    <span>Load Calculator</span>
                </button>
                <button
                    type="button"
                    className="obd-merged-system__icon-btn"
                    onClick={() => setModalOpen(true)}
                    disabled={editUtilityBlocked}
                    title={
                        isRoofOnlyOpportunity && !isUtilityDisabled
                            ? 'Utility details cannot be edited when the opportunity includes only a roof product.'
                            : undefined
                    }
                    aria-label="Edit utility details"
                >
                    <PencilSimple size={16} weight="regular" />
                </button>
            </div>
        ),
        [editUtilityBlocked, isRoofOnlyOpportunity, isUtilityDisabled],
    );

    useEffect(() => {
        if (!isPopup || !onPopupToolbar) return;
        onPopupToolbar(utilityToolbar);
        return () => onPopupToolbar(null);
    }, [isPopup, onPopupToolbar, utilityToolbar]);

    const showFromMonthlyProductions = ((): boolean => {
        const su = solarUtilityData?.SolarUtility;
        if (!su) return false;
        const annualRaw = su.AnnualUsage;
        const annual = annualRaw != null && annualRaw !== '' ? Number(annualRaw) : NaN;
        if (Number.isFinite(annual) && annual > 0) return false;
        const hasMonthVal = MONTH_LONG.some((name) => (Number(su[name]) || 0) > 0);
        return hasMonthVal;
    })();

    const monthlyKwh = useMemo(() => {
        const su = solarUtilityData?.SolarUtility;
        if (!su) return [];
        return MONTH_LONG.map((name) => Number(su[name]) || 0);
    }, [solarUtilityData]);

    const chartHasData = monthlyKwh.some((v) => v > 0);

    const yearlyUsageNumeric = useMemo(() => {
        const su = solarUtilityData?.SolarUtility;
        if (!su) return 0;
        const derived = annualKwhFromMonthlyDailyFields(su as Record<string, unknown>, MONTH_LONG);
        const monthlyMode = su.EnterAnnualUsage === 'MonthlyUsageCont';
        const annualRaw = su.AnnualUsage;
        const explicit =
            annualRaw != null && annualRaw !== '' && Number.isFinite(Number(annualRaw)) && Number(annualRaw) > 0
                ? Number(annualRaw)
                : 0;
        if (monthlyMode && derived > 0) return derived;
        if (explicit > 0) return explicit;
        return derived;
    }, [solarUtilityData]);

    const showYearlyChart = !chartHasData && yearlyUsageNumeric > 0;

    const monthlyChartOptions = useMemo((): ApexOptions => {
        const isDark = theme === 'dark';
        const fore = isDark ? '#e2e8f0' : '#334155';
        const grid = isDark ? 'rgba(148,163,184,0.12)' : '#e2e8f0';
        return {
            chart: {
                type: 'bar',
                toolbar: { show: false },
                fontFamily: 'inherit',
                background: 'transparent',
            },
            plotOptions: {
                bar: {
                    borderRadius: 6,
                    columnWidth: '72%',
                    dataLabels: { position: 'top' },
                    distributed: true,
                },
            },
            dataLabels: {
                enabled: false,
            },
            xaxis: {
                categories: MONTH_SHORT,
                labels: { style: { colors: fore, fontSize: '10px', fontWeight: 600 } },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                labels: {
                    style: { colors: fore, fontSize: '10px' },
                    formatter: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v))),
                },
            },
            grid: { borderColor: grid, strokeDashArray: 4, padding: { top: 8, right: 8, bottom: 0, left: 8 } },
            colors: MONTHLY_BAR_COLORS,
            legend: { show: false },
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                y: { formatter: (v: number) => `${v.toLocaleString()} kWh/day` },
            },
        };
    }, [theme]);

    const chartSeries = useMemo(
        () => [{ name: 'Daily (kWh/day)', data: monthlyKwh }],
        [monthlyKwh]
    );

    const yearlyChartOptions = useMemo((): ApexOptions => {
        const isDark = theme === 'dark';
        const fore = isDark ? '#e2e8f0' : '#334155';
        const grid = isDark ? 'rgba(148,163,184,0.12)' : '#e2e8f0';
        return {
            chart: {
                type: 'bar',
                toolbar: { show: false },
                fontFamily: 'inherit',
                background: 'transparent',
            },
            plotOptions: {
                bar: {
                    borderRadius: 10,
                    columnWidth: '42%',
                    dataLabels: { position: 'top' },
                },
            },
            dataLabels: {
                enabled: true,
                formatter: (val: number) => (val >= 1000 ? `${(val / 1000).toFixed(1)}k` : String(Math.round(val))),
                style: { fontSize: '12px', fontWeight: 700 },
                offsetY: -4,
            },
            xaxis: {
                categories: ['Annual usage'],
                labels: { style: { colors: fore, fontSize: '11px', fontWeight: 600 } },
                axisBorder: { show: false },
                axisTicks: { show: false },
            },
            yaxis: {
                min: 0,
                max: (max: number) => {
                    const m = typeof max === 'number' && Number.isFinite(max) ? max : 0;
                    if (m <= 0) return undefined as unknown as number;
                    return Math.ceil(Math.max(m * 1.12, m + m * 0.04));
                },
                labels: {
                    style: { colors: fore, fontSize: '10px' },
                    formatter: (v: number) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v))),
                },
            },
            grid: { borderColor: grid, strokeDashArray: 4, padding: { top: 28, right: 16, bottom: 4, left: 8 } },
            colors: ['var(--primary)'],
            fill: { type: 'solid', opacity: 1 },
            legend: { show: false },
            tooltip: {
                theme: isDark ? 'dark' : 'light',
                y: { formatter: (v: number) => `${Number(v).toLocaleString()} kWh` },
            },
        };
    }, [theme]);

    const yearlyChartSeries = useMemo(
        () => [{ name: 'Annual (kWh)', data: [yearlyUsageNumeric] }],
        [yearlyUsageNumeric]
    );

    useEffect(() => {
        const su = solarUtilityData?.SolarUtility;
        if (!su) return;
        const derived = annualKwhFromMonthlyDailyFields(su as Record<string, unknown>, MONTH_LONG);
        const monthlyMode = su.EnterAnnualUsage === 'MonthlyUsageCont';
        const explicitRaw = su.AnnualUsage != null && su.AnnualUsage !== '' ? Number(su.AnnualUsage) : NaN;
        const hasExplicit = Number.isFinite(explicitRaw) && explicitRaw > 0;

        let displayAnnual = 0;
        if (monthlyMode) {
            displayAnnual = derived > 0 ? derived : hasExplicit ? explicitRaw : 0;
        } else if (hasExplicit) {
            displayAnnual = explicitRaw;
        } else if (derived > 0) {
            displayAnnual = derived;
        }

        if (displayAnnual > 0) {
            setAnnualUsageKwh(displayAnnual.toLocaleString());
            setAvgDailyKwh((displayAnnual / 365).toFixed(1));
        } else {
            setAnnualUsageKwh('');
            setAvgDailyKwh('');
        }
        if (su.EstAnnualUtilityIncrease != null && su.EstAnnualUtilityIncrease !== '') {
            setEstIncreaseChecked(true);
            const pct = Number(su.EstAnnualUtilityIncrease);
            setEstIncrease(Number.isFinite(pct) ? String(pct) : String(su.EstAnnualUtilityIncrease));
        }
        if (su.CurrentAllInUtilityRate != null && su.CurrentAllInUtilityRate !== '') {
            const rate = Number(su.CurrentAllInUtilityRate);
            setAllInRate(Number.isFinite(rate) ? String(rate) : String(su.CurrentAllInUtilityRate));
        }
    }, [solarUtilityData]);

    const shellClassName = isPopup
        ? `obd-utility--popup obd-theme-${theme} ${isUtilityDisabled ? 'obd-disabled-section' : ''}`
        : `obd-card obd-card--utility obd-dashboard-main-card obd-theme-${theme} ${isUtilityDisabled ? 'obd-disabled-section' : ''}`;

    return (
        <>
            <div className={shellClassName}>
                {!isPopup ? (
                    <div className="obd-utility__head obd-dashboard-main-card__head">
                        <div className="obd-utility__head-start">
                            <span className="obd-section-head-icon obd-section-head-icon--sm" aria-hidden>
                                <ChartBar size={18} weight="duotone" />
                            </span>
                            <span className="obd-utility__header">Utility Details</span>
                            {utilityDetailsDirty && (
                                <span
                                    className="obd-utility__unsaved-chip"
                                    title="You have unsaved changes. Open Utility details and click Save."
                                >
                                    Unsaved
                                </span>
                            )}
                        </div>
                        <div className="obd-metrics__header-actions">{utilityToolbar}</div>
                    </div>
                ) : null}
                <div
                    className={`obd-dashboard-main-card__body${isPopup ? ' obd-dashboard-main-card__body--utility-popup' : ''}`}
                >
                {(chartHasData || showYearlyChart) && (
                    <div className="obd-utility__chart-wrap">
                        <div className="obd-utility__chart-title-block">
                            <span className="obd-utility__chart-title">
                                {chartHasData ? 'Daily usage by month (kWh/day)' : 'Yearly usage (kWh)'}
                            </span>
                            {chartHasData && (
                                <span className="obd-utility__chart-subtitle">
                                    Annual kWh = sum of (each month’s daily kWh × days in that month).
                                </span>
                            )}
                            {!chartHasData && showYearlyChart && (
                                <span className="obd-utility__chart-subtitle">
                                    Customer annual electrical load — qualifying designs should not exceed this production.
                                </span>
                            )}
                        </div>
                        {chartHasData ? (
                            <Chart options={monthlyChartOptions} series={chartSeries} type="bar" height={220} />
                        ) : (
                            <Chart options={yearlyChartOptions} series={yearlyChartSeries} type="bar" height={200} />
                        )}
                    </div>
                )}
                <div className="obd-utility__overlays obd-utility__overlays--row">
                    <div className="obd-utility__cell obd-utility__cell--row">
                        <span className="obd-utility__cell-label">
                            <Buildings size={14} weight="duotone" className="obd-utility__cell-icon" aria-hidden />
                            Utility company
                        </span>
                        <span className="obd-utility__cell-value">
                            {utilityCompanyName ??
                                ((typeof solarUtilityData?.SolarUtility?.UtilityCompanyName === 'string'
                                    ? solarUtilityData.SolarUtility.UtilityCompanyName
                                    : typeof solarUtilityData?.SolarUtility?.UtilityCompany === 'string'
                                      ? solarUtilityData.SolarUtility.UtilityCompany
                                      : '') ||
                                    '—')}
                        </span>
                    </div>
                    <div className="obd-utility__cell obd-utility__cell--row obd-utility__cell--upload">
                        <span className="obd-utility__cell-label">
                            <FileArrowUp size={14} weight="duotone" className="obd-utility__cell-icon" aria-hidden />
                            Uploaded files
                        </span>
                        <span className="obd-utility__cell-value">
                            {solarUtilityData?.SolarUtility.FilePath != null &&
                            solarUtilityData?.SolarUtility.FilePath !== '' ? (
                                <a href={solarUtilityData.SolarUtility.FilePath} target="_blank" rel="noreferrer">
                                    {solarUtilityData.SolarUtility.FileName}
                                </a>
                            ) : (
                                'No file'
                            )}
                        </span>
                    </div>
                    {!showYearlyChart ? (
                        <div className="obd-utility__cell">
                            <span className="obd-utility__cell-label">
                                <Lightning size={14} weight="duotone" className="obd-utility__cell-icon" aria-hidden />
                                ANNUAL USAGE (KWH)
                            </span>
                            <span className="obd-utility__cell-value">{annualUsageKwh ?? ''}</span>
                            {showFromMonthlyProductions && (annualUsageKwh ?? '') !== '' && (
                                <span className="obd-utility__cell-sub obd-utility__cell-sub--block">
                                    From monthly values
                                </span>
                            )}
                        </div>
                    ) : null}
                    <div className="obd-utility__cell obd-utility__cell--row">
                        <span className="obd-utility__cell-label">
                            <TrendUp size={14} weight="duotone" className="obd-utility__cell-icon" aria-hidden />
                            Est. increase
                        </span>
                        <span className="obd-utility__cell-value">
                            {estIncreaseChecked && estIncrease ? `${estIncrease}%` : '—'}
                        </span>
                    </div>
                    <div className="obd-utility__cell obd-utility__cell--row">
                        <span className="obd-utility__cell-label">
                            <ChartBar size={14} weight="duotone" className="obd-utility__cell-icon" aria-hidden />
                            Avg daily (kWh)
                        </span>
                        <span className="obd-utility__cell-value">{avgDailyKwh ?? '—'}</span>
                    </div>
                    <div className="obd-utility__cell obd-utility__cell--row">
                        <span className="obd-utility__cell-label">
                            <Receipt size={14} weight="duotone" className="obd-utility__cell-icon" aria-hidden />
                            All-in rate
                        </span>
                        <span className="obd-utility__cell-value">{allInRate ?? '—'}</span>
                    </div>
                </div>
                </div>
            </div>
            <UtilityLoadCalculatorModal open={loadCalculatorOpen} onClose={() => setCalculatorOpen(false)} opportunityId={opportunityDetails?.Opportunity_Id}/>

            {modalOpen && (
                <UtilityDetailsModal
                    oppId={sessionStorage.getItem('SalesForceId')}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    annualUsageKwh={annualUsageKwh}
                    onAnnualUsageKwhChange={setAnnualUsageKwh}
                    estIncrease={estIncrease}
                    estIncreaseChecked={estIncreaseChecked}
                    onEstIncreaseCheckedChange={setEstIncreaseChecked}
                    onEstIncreaseChange={setEstIncrease}
                    avgDailyKwh={avgDailyKwh}
                    onAvgDailyKwhChange={setAvgDailyKwh}
                    allInRate={allInRate}
                    onAllInRateChange={setAllInRate}
                />
            )}
        </>
    );
};

export default UtilityDetailsCard;
